import { path, is, isEmpty } from 'ramda'
import { t } from 'i18next'
import { LocalStorageKeys, provinces, uniqueKey, listOrdersType } from '../constants'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import { ItemHasChildrenType, ItemType, Option, Phone, Role } from '../types'
import { Member, NonMemberCompany } from '../types/api/user'
import { FormationCategory } from '../types/api/formations'
import { FormikProps } from 'formik'
import { ErrorApiFieldType, OptionalParamsApi } from '../types/api'
import { v4 as uuidv4 } from 'uuid'

export const getNestedObject = (keyPath: string, obj: Record<string, any>) =>
  path(keyPath.split('.'), obj)
export const isString = (value: any): value is string => is(String, value)
export const getProvinces = (): Option[] =>
  provinces.map((province) => ({ id: province, name: t(`provinces.${province}`) }))
export const getDay = (date: string) => (date ? dayjs(date).format('DD') : '')
export const getMonthName = (date: string) => {
  dayjs.locale('fr')
  return date ? dayjs(date).format('MMMM') : ''
}
export const getYear = (date: string) => (date ? dayjs(date).format('YYYY') : '')
export const getDayAndMonthName = (date: string) => `${getDay(date)} ${getMonthName(date)}`
export const getDayAndMonthNameYear = (date: string) =>
  `${getDay(date)} ${getMonthName(date)} ${getYear(date)}`
export const getFormatDate = (date: string) => (date ? dayjs(date).format('YYYY-MM-DD') : '')

export const formatTime = (time: string | undefined): string => {
  if (!time) return ''
  return time.split(':').slice(0, 2).join(':')
}

export const formatTimeFromAny = (timeOrDatetime: string | undefined): string => {
  if (!timeOrDatetime) return ''
  if (timeOrDatetime.includes(' ')) {
    const parts = timeOrDatetime.split(' ')
    return formatTime(parts[1])
  }
  
  return formatTime(timeOrDatetime)
}

export const formatDateFromAny = (dateOrDatetime: string | undefined): string => {
  if (!dateOrDatetime) return ''

  const datePart = dateOrDatetime.split(' ')[0]
  
  const dateMatch = datePart.match(/(\d{2})-(\d{2})-(\d{4})/)
  if (dateMatch) {
    const day = dateMatch[1]
    const month = parseInt(dateMatch[2]) - 1 
    const year = dateMatch[3]
    return `${day} ${monthNames[month]} ${year}`
  }
  
  return getDate(datePart, null) || datePart
}

const monthNames = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const formatDate = (date : string, text:string) => {
  if (!text) return date;
  const format1 = date.match(/(\d{2})-(\d{2})-(\d{4})/);
  if (format1) {
      text = text.replace('Y', format1[3]);
      text = text.replace('m', format1[2]);
      text = text.replace('M', monthNames[Number(format1[2]) - 1]);
      text = text.replace('d', format1[1]);
      return text;
  }
  const format2 = date.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (format2) {
      text = text.replace('Y', format2[1]);
      text = text.replace('m', format2[2]);
      text = text.replace('M', monthNames[Number(format2[2]) - 1]);
      text = text.replace('d', format2[3]);
      return text;
  }
}
export const getDate = (start : any, end : any) => {
  end = end ? end : start;
  const result = formatDate(end, 'd M Y');
  if (result == formatDate(start, 'd M Y')) {
      return result;
  } else {
      if (formatDate(start, 'M Y') == formatDate(end, 'M Y')) {
          return formatDate(start, 'd') + ' au ' + result;
      } else {
          if (formatDate(start, 'Y') == formatDate(end, 'Y')) {
              return formatDate(start, 'd M') + ' au ' + result;
          } else {
              return formatDate(start, 'd M Y') + ' au ' + result;
          }
      }
  }
}

export const getDetailedDate = (start : any, end : any) => {
  end = end ? end : start;
  const startFormatted = formatDate(start, 'd M Y');
  const endFormatted = formatDate(end, 'd M Y');
  
  if (startFormatted == endFormatted) {
      return startFormatted;
  } else {
      return startFormatted + ' au ' + endFormatted;
  }
}


export const formatNumber = (number: string) =>
  parseInt(number)
    .toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, ' ')
export const extractMainPhoneAndOthers = (phones: Phone[]) => ({
  mainPhone: phones?.[0] || [],
  otherPhones: phones?.slice(1) || [],
  otherPhones1: phones?.[1] || [],
  otherPhones2: phones?.[2] || [],
})
export const getTitle = (title: string, listTitles: Option[] | undefined) =>
  Array.isArray(listTitles) && listTitles?.length > 0
    ? listTitles?.find((option) => option.id === title)?.name
    : ''
// Libellés de tous les titres d'un contact (titre_c est un choix multiple dans le CRM).
// Une clé absente de la liste est affichée telle quelle plutôt que masquée.
export const getTitles = (titles: string[] | undefined, listTitles: Option[] | undefined) =>
  (titles || []).map((title) => getTitle(title, listTitles) || title)
export const getActiveEnterpriseFromLocalStorage = () => {
  const activeEnterprise = localStorage.getItem(LocalStorageKeys.ActiveEnterprise)
  return (activeEnterprise ? JSON.parse(activeEnterprise) : {}) as Member
}

// Construit un objet de la forme Member à partir d'une NonMemberCompany,
// pour pouvoir la faire transiter par ActiveEnterpriseContext (et donc par
// toutes les pages /entreprise/* existantes) sans les modifier une par une -
// voir discussion avec le client, 2026-09-24. is_real_member: false est le
// seul marqueur qui distingue ce cas, utilisé uniquement pour cacher
// "Avantages Membres" (memberBenefits).
export const synthesizeMemberFromNonMemberCompany = (company: NonMemberCompany): Member => ({
  crm_id: company.crm_id,
  legal_name: company.legal_name,
  region: company.region as Member['region'],
  roles: company.roles,
  cart_item: null,
  display_renewal_button: false,
  renew_membership_product_crm_id: '',
  expired_membership: false,
  status: 'Actif',
  resignation_date: null,
  is_real_member: false,
})
export const hasChildren = (item: ItemType | ItemHasChildrenType): item is ItemHasChildrenType =>
  'children' in item

export const isMember = (activeEnterprise: Member) => {
  if (isEmpty(activeEnterprise) || activeEnterprise.status !== 'Actif') {
    return false
  }
  
  // Check if resignation date exists and if it's in the past
  if (activeEnterprise.resignation_date) {
    const resignationDate = new Date(activeEnterprise.resignation_date)
    const now = new Date()
    if (resignationDate <= now) {
      return false
    }
  }
  
  return true
}
export const isAnyRoleGranted = (
  activesEntrepriseRoles: Role[] | null,
  rolesGranted: Role[] | null,
) => {
  if (rolesGranted === null) return true
  if (activesEntrepriseRoles === null) return false
  return activesEntrepriseRoles.some((role) => rolesGranted?.includes(role))
}
export const hasAccess = (rolesGranted: null | Role[], activeEnterprise: Member) => {
  let nonMemberAccess = false

  if (rolesGranted === null) {
    nonMemberAccess = true
  }
  else {
    nonMemberAccess = activeEnterprise.legal_name === undefined && rolesGranted.includes('Non-member')
  }

  const nonMemberHasAccess = nonMemberAccess
  return (
    nonMemberHasAccess ||
    (isMember(activeEnterprise) && isAnyRoleGranted(activeEnterprise.roles, rolesGranted))
  )
}
export const handleCustomErrorsFormik = (formik: FormikProps<any>, errors: ErrorApiFieldType) => {
  Object.entries(errors).forEach(([key, value]) => {
    formik.setFieldError(key, value[0])
  })
}

const isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
  input !== null && input.tagName === 'IFRAME'

export function postCrossDomainMessage(msg: string) {
  const frame = document.getElementById('ifr')
  if (isIFrame(frame) && frame.contentWindow) {
    frame.contentWindow.postMessage(msg, `${process.env.REACT_APP_ACQ_SITE as string}/cross.php`)
  }
}
export const generateParamsURL = ({ member_crm_id, page, sort, filter, paginated }: OptionalParamsApi) => {
  const params = new URLSearchParams({
    ...(member_crm_id ? { member_crm_id } : {}),
    page_size: '10',
    ...(page ? { page } : {}),
    sort_by: sort.id,
    asc: sort.desc ? '0' : '1',
    ...(paginated ? { paginated } : {}),
  })
  if (filter)
    Object.entries(filter).forEach(([key, value]) => {
      params.set(key, value)
    })
  return params.toString()
}
export const addUniqueKeyToList = <T>(list: T[] | undefined | null) => {
  if (!list) return []
  return list.map((item: T) => {
    return { ...item, [uniqueKey]: uuidv4() }
  })
}

export const getOrderType = (type: string) => {
 return listOrdersType.filter(item => {
  return item.id === type
 })[0]?.name
}

export const containsRBQ = (str: string | undefined): boolean => {
  if (!str) return false
  return str.toLowerCase().includes('rbq')
}

export const shouldShowChangeDatesButton = (startDate: string | null | undefined, startTime: string, category: string | undefined): boolean => {
  
  if (!startDate) return false
  
  // for show button even if the time is not provided, we consider the start time as 00:00 to calculate the cutoff date
  const timeOnly = startTime || '00:00'
  const formationDateTime = dayjs(`${startDate} ${timeOnly}`, 'DD-MM-YYYY HH:mm')
  
  const now = dayjs()
  let cutoffWeeks = 1

  if (category === FormationCategory.ASP || category === FormationCategory.FPRBQ || containsRBQ(category)) {
    cutoffWeeks = 2
  }
  const cutoffDateTime = formationDateTime.subtract(cutoffWeeks, 'week')
  return now.isBefore(cutoffDateTime)
}
